import { LOOKUP_FALLBACKS } from "./lookups";
import { todayIndiaIsoDate } from "./indiaTime";

export const PARTY_KIND_OPTIONS = [
  { value: "FARMER", label: "Farmer" },
  { value: "SUPPLIER", label: "Supplier" },
];

export const CONTRACT_PHASES = [
  { value: "PRE_ARRIVAL", label: "Pre Arrival" },
  { value: "ARRIVAL", label: "Arrival" },
];

export const PURCHASE_TYPES_PRE_ARRIVAL = [
  { value: "MANDI_PURCHASE", label: "Mandi Purchase" },
  { value: "STOCK_PURCHASE", label: "Stock Purchase" },
  { value: "FOR_FACTORY", label: "FOR factory (supplier)" },
];

export const PURCHASE_TYPES_ARRIVAL = [
  { value: "ON_ARRIVAL_DIRECT_PURCHASE", label: "On Arrival Direct Purchase" },
  { value: "MANDI_PURCHASE", label: "Mandi Purchase" },
  { value: "STOCK_PURCHASE", label: "Stock Purchase" },
  { value: "FOR_FACTORY", label: "FOR factory (supplier)" },
];

/** @deprecated use purchaseTypesForPhase */
export const PURCHASE_TYPES = PURCHASE_TYPES_PRE_ARRIVAL;

export function purchaseTypesForPhase(phase) {
  return phase === "ARRIVAL" ? PURCHASE_TYPES_ARRIVAL : PURCHASE_TYPES_PRE_ARRIVAL;
}

export const PURCHASE_BROKERAGE_BASIS_OPTIONS = [
  { value: "NONE", label: "Not applicable" },
  { value: "GROSS_PCT", label: "% of gross" },
  { value: "PER_QTL", label: "Per qtl" },
  { value: "PER_BAG", label: "Per bag" },
  { value: "FIXED_AMOUNT", label: "Fixed amount" },
];

export const PARTY_BROKERAGE_COLLECTION_OPTIONS = [
  { value: "DEDUCT_FROM_LEDGER", label: "Deduct from party ledger" },
  { value: "DIRECT_COLLECT", label: "Party collects direct" },
];

function splitBrokeragePayload(form) {
  const side = (basis, rate) => {
    const normalized = basis && basis !== "NONE" ? basis : null;
    const numeric = rate !== "" && rate != null ? Number(rate) : null;
    return {
      basis: normalized,
      rate: normalized && numeric != null && !Number.isNaN(numeric) ? numeric : null,
    };
  };
  const our = side(form.our_side_brokerage_basis, form.our_side_brokerage_rate);
  const party = side(form.party_side_brokerage_basis, form.party_side_brokerage_rate);
  return {
    our_side_brokerage_basis: our.basis,
    our_side_brokerage_rate: our.rate,
    party_side_brokerage_basis: party.basis,
    party_side_brokerage_rate: party.rate,
    party_side_collection_method:
      party.basis && form.party_side_collection_method ? form.party_side_collection_method : null,
  };
}

export function isPreArrivalPhase(phase) {
  return phase !== "ARRIVAL";
}

export function isArrivalPhase(phase) {
  return phase === "ARRIVAL";
}

export function isOnArrivalDirectPurchase(form) {
  return isArrivalPhase(form?.contract_phase) && form?.purchase_type === "ON_ARRIVAL_DIRECT_PURCHASE";
}

/** Master paddy / rice item list (aligned with gate pass varieties). */
export const QUALITY_ITEM_OPTIONS = LOOKUP_FALLBACKS.paddy_variety;

/** Options for quality/item dropdown — gate pass variety first, then master list. */
export function qualityItemOptionsForGatePass(gatePassId, linkableGatePasses = [], varietyOptions = QUALITY_ITEM_OPTIONS) {
  const gp = linkableGatePasses.find((g) => String(g.id) === String(gatePassId));
  const variety = (gp?.expected_variety || "").trim();
  const seen = new Set();
  const out = [];
  if (variety) {
    seen.add(variety);
    out.push({ value: variety, label: `${variety} (from gate pass)` });
  }
  for (const o of varietyOptions) {
    if (!seen.has(o.value)) {
      seen.add(o.value);
      out.push(o);
    }
  }
  return out;
}

/** Dropdown options for quality — gate pass varieties plus any saved values not in the master list. */
export function qualitySelectOptions(gatePassId, linkableGatePasses = [], varietyOptions = QUALITY_ITEM_OPTIONS, currentValues = []) {
  const base = qualityItemOptionsForGatePass(gatePassId, linkableGatePasses, varietyOptions);
  const seen = new Set(base.map((o) => o.value));
  const extras = [];
  for (const raw of currentValues) {
    const value = String(raw || "").trim();
    if (value && !seen.has(value)) {
      seen.add(value);
      extras.push({ value, label: value });
    }
  }
  return [...extras, ...base];
}

export const PAYMENT_CONDITION_OPTIONS = LOOKUP_FALLBACKS.payment_condition;
export const BARDANA_OPTIONS = LOOKUP_FALLBACKS.bardana;
export const BARDANA_DEDUCTION_OPTIONS = LOOKUP_FALLBACKS.bardana_deduction;
export const DELIVERY_OPTIONS = LOOKUP_FALLBACKS.delivery_condition;
export const CD_PERCENT_OPTIONS = LOOKUP_FALLBACKS.cd_percent;

export function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label ?? value ?? "—";
}

export function phaseLabel(phase) {
  return labelFor(CONTRACT_PHASES, phase);
}

export function partyKindLabel(kind) {
  return labelFor(PARTY_KIND_OPTIONS, kind);
}

export function partyLabel(row) {
  if (!row) return "—";
  const name = row.party_display_name || row.party_name;
  if (!name) return "—";
  const kind = row.party_kind ? partyKindLabel(row.party_kind) : null;
  return kind ? `${name} (${kind})` : name;
}

/** Compact label for contract select trigger (single line, truncated). */
export function contractSelectShortLabel(c) {
  if (!c) return "—";
  const qty = c.quantity != null && c.quantity !== "" ? `${c.quantity} qtl` : "—";
  return `${c.contract_number} · ${c.quality || "—"} · ${qty} · ₹${c.price ?? "—"}`;
}

/** One-line option in contract dropdown — balance/reserved, not full party text. */
export function contractSelectOptionLabel(c) {
  if (!c) return "—";
  const qty = c.quantity != null && c.quantity !== "" ? `${c.quantity} qtl` : "—";
  const bal = c.balance_qtl != null ? ` · bal ${c.balance_qtl}` : "";
  const reserved = c.reserved_qtl > 0 ? ` · ${c.reserved_qtl} reserved` : "";
  return `${c.contract_number} — ${c.quality || "—"} · ${qty}${bal}${reserved} · ₹${c.price ?? "—"}`;
}

export function paymentLabel(row) {
  return row.payment_condition_label || labelFor(PAYMENT_CONDITION_OPTIONS, row.payment_condition);
}

export function todayIsoDate() {
  return todayIndiaIsoDate() || "";
}

export const emptyPurchaseContractForm = () => ({
  contract_phase: "PRE_ARRIVAL",
  contract_date: todayIsoDate(),
  party_kind: "FARMER",
  party_id: "",
  purchase_type: "MANDI_PURCHASE",
  gate_pass_id: "",
  quality: "",
  quantity: "",
  price: "",
  bardana: "BAG_40KG",
  bardana_deduction: "GM_500_PER_QTL",
  bardana_return: true,
  weight_basis: "FIXED_PER_BAG",
  delivery_condition: "TRUCK_LOAD",
  freight_rate_per_qtl: "",
  cd_applicable: false,
  cd_percent: "1",
  broker_applicable: false,
  broker_party_id: "",
  brokerage: "",
  our_side_brokerage_basis: "NONE",
  our_side_brokerage_rate: "",
  party_side_brokerage_basis: "NONE",
  party_side_brokerage_rate: "",
  party_side_collection_method: "DEDUCT_FROM_LEDGER",
  payment_condition: "SAME_DAY",
  payment_condition_other: "",
  stock_sample_analysed: "",
  stack_sample_lab_report_id: "",
  quality_conditions: "",
});

/** Map API row → form state for edit dialog. */
export function contractToForm(c) {
  if (!c) return emptyPurchaseContractForm();
  const phase = c.contract_phase || "PRE_ARRIVAL";
  return {
    contract_phase: phase,
    contract_date: c.contract_date || todayIsoDate(),
    party_kind: c.party_kind || "FARMER",
    party_id: c.party_id ? String(c.party_id) : "",
    purchase_type: c.purchase_type || (phase === "ARRIVAL" ? "ON_ARRIVAL_DIRECT_PURCHASE" : "MANDI_PURCHASE"),
    gate_pass_id: c.gate_pass_id ? String(c.gate_pass_id) : "",
    quality: c.quality || "",
    quantity: c.quantity != null ? String(c.quantity) : "",
    price: c.price != null ? String(c.price) : "",
    bardana: c.bardana || "BAG_40KG",
    bardana_deduction: c.bardana_deduction || "GM_500_PER_QTL",
    bardana_return: c.bardana_return !== false,
    weight_basis: c.weight_basis || "FIXED_PER_BAG",
    delivery_condition: c.delivery_condition || (phase === "ARRIVAL" ? "FOR" : "TRUCK_LOAD"),
    freight_rate_per_qtl: c.freight_rate_per_qtl != null ? String(c.freight_rate_per_qtl) : "",
    cd_applicable: Boolean(c.cd_applicable),
    cd_percent: c.cd_percent != null ? String(c.cd_percent) : "1",
    broker_applicable: Boolean(c.broker_applicable),
    broker_party_id: c.broker_party_id ? String(c.broker_party_id) : "",
    brokerage: c.brokerage != null ? String(c.brokerage) : "",
    our_side_brokerage_basis: c.our_side_brokerage_basis || "NONE",
    our_side_brokerage_rate: c.our_side_brokerage_rate != null ? String(c.our_side_brokerage_rate) : "",
    party_side_brokerage_basis: c.party_side_brokerage_basis || "NONE",
    party_side_brokerage_rate: c.party_side_brokerage_rate != null ? String(c.party_side_brokerage_rate) : "",
    party_side_collection_method: c.party_side_collection_method || "DEDUCT_FROM_LEDGER",
    payment_condition: c.payment_condition || "SAME_DAY",
    payment_condition_other: c.payment_condition_other || "",
    stock_sample_analysed:
      c.stock_sample_analysed === true ? "yes" : c.stock_sample_analysed === false ? "no" : "",
    stack_sample_lab_report_id: c.stack_sample_lab_report_id ? String(c.stack_sample_lab_report_id) : "",
    quality_conditions: c.quality_conditions || "",
  };
}

/** Build API payload from form state. */
export function buildPurchaseContractPayload(form) {
  const phase = form.contract_phase || "PRE_ARRIVAL";
  const isStock = form.purchase_type === "STOCK_PURCHASE";
  const sampleAnalysed = form.stock_sample_analysed === "yes";
  const isArrival = phase === "ARRIVAL";
  const onArrivalDirect = isOnArrivalDirectPurchase(form);

  const payload = {
    contract_phase: phase,
    contract_date: form.contract_date || todayIsoDate(),
    party_kind: form.party_kind,
    party_id: Number(form.party_id),
    purchase_type: form.purchase_type,
    quality: form.quality.trim(),
    quantity: onArrivalDirect ? 0 : Number(form.quantity),
    price: Number(form.price),
    bardana: form.bardana,
    ...(form.purchase_type !== "MANDI_PURCHASE" ? { bardana_deduction: form.bardana_deduction } : {}),
    ...(form.purchase_type === "FOR_FACTORY" ? { bardana_return: form.bardana_return !== false } : {}),
    weight_basis: form.purchase_type === "MANDI_PURCHASE" ? form.weight_basis || "FIXED_PER_BAG" : null,
    delivery_condition: form.delivery_condition,
    freight_rate_per_qtl: form.freight_rate_per_qtl !== "" ? Number(form.freight_rate_per_qtl) : null,
    cd_applicable: form.cd_applicable,
    cd_percent: form.cd_applicable ? Number(form.cd_percent) : null,
    broker_applicable: form.broker_applicable,
    broker_party_id: form.broker_applicable ? Number(form.broker_party_id) : null,
    brokerage:
      form.broker_applicable && form.brokerage !== "" && form.brokerage != null
        ? Number(form.brokerage)
        : null,
    ...splitBrokeragePayload(form),
    payment_condition: form.payment_condition,
    payment_condition_other: form.payment_condition === "OTHER" ? form.payment_condition_other.trim() : null,
    quality_conditions: form.quality_conditions.trim() || null,
  };

  if (isArrival) {
    payload.gate_pass_id = Number(form.gate_pass_id);
  }

  if (!isArrival && isStock) {
    payload.stock_sample_analysed = sampleAnalysed;
    if (sampleAnalysed) {
      payload.stack_sample_lab_report_id = Number(form.stack_sample_lab_report_id);
    }
  }

  return payload;
}

export function onContractPhaseChange(prevForm, newPhase) {
  const types = purchaseTypesForPhase(newPhase);
  const nextType = types.some((t) => t.value === prevForm.purchase_type)
    ? prevForm.purchase_type
    : types[0].value;
  const next = {
    ...prevForm,
    contract_phase: newPhase,
    purchase_type: nextType,
    gate_pass_id: newPhase === "ARRIVAL" ? prevForm.gate_pass_id : "",
    delivery_condition: nextType === "FOR_FACTORY" || newPhase === "ARRIVAL" ? "FOR" : "TRUCK_LOAD",
    party_kind: nextType === "FOR_FACTORY" ? "SUPPLIER" : prevForm.party_kind,
    party_id: nextType === "FOR_FACTORY" && prevForm.party_kind !== "SUPPLIER" ? "" : prevForm.party_id,
    stock_sample_analysed: "",
    stack_sample_lab_report_id: "",
  };
  if (nextType === "ON_ARRIVAL_DIRECT_PURCHASE") {
    next.quantity = "";
  }
  return next;
}

export function onPurchaseTypeChange(prevForm, newType, linkableGatePasses = []) {
  const next = {
    ...prevForm,
    purchase_type: newType,
    stock_sample_analysed: "",
    stack_sample_lab_report_id: "",
    quantity: newType === "ON_ARRIVAL_DIRECT_PURCHASE" ? "" : prevForm.quantity,
  };
  if (newType === "FOR_FACTORY") {
    next.delivery_condition = "FOR";
    if (prevForm.bardana_return == null) next.bardana_return = true;
    if (prevForm.party_kind !== "SUPPLIER") {
      next.party_kind = "SUPPLIER";
      next.party_id = "";
    }
  }
  if (newType === "ON_ARRIVAL_DIRECT_PURCHASE" && prevForm.gate_pass_id) {
    const gp = linkableGatePasses.find((g) => String(g.id) === String(prevForm.gate_pass_id));
    if (gp?.expected_variety) {
      next.quality = gp.expected_variety;
    }
  }
  return next;
}

export function onPartyKindChange(prevForm, newKind) {
  return { ...prevForm, party_kind: newKind, party_id: "" };
}

export function onGatePassChange(prevForm, gatePassId, linkableGatePasses = []) {
  if (!gatePassId) {
    const next = { ...prevForm, gate_pass_id: "" };
    if (isOnArrivalDirectPurchase(prevForm)) {
      next.party_id = "";
      next.party_kind = prevForm.party_kind || "FARMER";
      next.quality = "";
      if (Array.isArray(prevForm.items)) {
        next.items = prevForm.items.map((line) => ({ ...line, quality: "" }));
      }
    }
    return next;
  }
  const gp = linkableGatePasses.find((g) => String(g.id) === String(gatePassId));
  const next = { ...prevForm, gate_pass_id: gatePassId };
  if (gp?.party_id) {
    // Party comes from the gate pass (farmer or supplier).
    next.party_kind = gp.party_kind === "SUPPLIER" ? "SUPPLIER" : "FARMER";
    next.party_id = String(gp.party_id);
  } else if (isOnArrivalDirectPurchase(prevForm)) {
    next.party_id = "";
    next.party_kind = prevForm.party_kind || "FARMER";
  }
  if (isOnArrivalDirectPurchase(next) && gp?.expected_variety) {
    next.quality = gp.expected_variety;
    if (Array.isArray(prevForm.items) && prevForm.items.length > 0) {
      next.items = prevForm.items.map((line, i) =>
        i === 0 && !String(line.quality || "").trim()
          ? { ...line, quality: gp.expected_variety }
          : line,
      );
    }
  }
  return next;
}

/** Arrival direct: party (farmer or supplier) is taken from the gate pass. */
export function gatePassSetsFarmer(form) {
  return isArrivalPhase(form?.contract_phase) && isOnArrivalDirectPurchase(form);
}

/** Alias — party is locked from the selected inward pass. */
export function gatePassSetsParty(form) {
  return gatePassSetsFarmer(form);
}

export function partiesForKind(partyKind, farmers = [], suppliers = []) {
  return partyKind === "SUPPLIER" ? suppliers : farmers;
}

/** Shared fields for farmer on-arrival direct shortcuts. */
const FARMER_ARRIVAL_DIRECT_BASE = {
  contract_phase: "ARRIVAL",
  party_kind: "FARMER",
  purchase_type: "ON_ARRIVAL_DIRECT_PURCHASE",
  delivery_condition: "FOR",
  bardana: "LOOSE",
  bardana_deduction: "KG_1_PER_QTL",
  broker_applicable: false,
  broker_party_id: "",
  brokerage: "",
  our_side_brokerage_basis: "NONE",
  our_side_brokerage_rate: "",
  party_side_brokerage_basis: "NONE",
  party_side_brokerage_rate: "",
  party_side_collection_method: "DEDUCT_FROM_LEDGER",
  quantity: "",
  freight_rate_per_qtl: "",
  stock_sample_analysed: "",
  stack_sample_lab_report_id: "",
  quality_conditions: "",
};

const SUPPLIER_ARRIVAL_DIRECT_BASE = {
  ...FARMER_ARRIVAL_DIRECT_BASE,
  party_kind: "SUPPLIER",
};

/** One-click presets on the contracts page (farmer + truck in yard). */
export const FARMER_ARRIVAL_CONTRACT_SHORTCUTS = [
  {
    id: "farmer-cd1-same-day",
    label: "CD 1% · Same day",
    description: "Arrival · On arrival direct · Loose · 1 kg/qtl · FOR",
    preset: {
      ...FARMER_ARRIVAL_DIRECT_BASE,
      cd_applicable: true,
      cd_percent: "1",
      payment_condition: "SAME_DAY",
      payment_condition_other: "",
    },
  },
  {
    id: "farmer-no-cd-8-10",
    label: "No CD · 8–10 days",
    description: "Arrival · On arrival direct · Loose · 1 kg/qtl · FOR",
    preset: {
      ...FARMER_ARRIVAL_DIRECT_BASE,
      cd_applicable: false,
      cd_percent: "1",
      payment_condition: "EIGHT_TO_TEN_DAYS",
      payment_condition_other: "",
    },
  },
];

/** Supplier on-arrival direct presets (yard truck must be linked to a supplier). */
export const SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS = [
  {
    id: "supplier-cd1-same-day",
    label: "CD 1% · Same day",
    description: "Arrival · On arrival direct · Supplier truck in yard · FOR",
    preset: {
      ...SUPPLIER_ARRIVAL_DIRECT_BASE,
      cd_applicable: true,
      cd_percent: "1",
      payment_condition: "SAME_DAY",
      payment_condition_other: "",
    },
  },
  {
    id: "supplier-no-cd-8-10",
    label: "No CD · 8–10 days",
    description: "Arrival · On arrival direct · Supplier truck in yard · FOR",
    preset: {
      ...SUPPLIER_ARRIVAL_DIRECT_BASE,
      cd_applicable: false,
      cd_percent: "1",
      payment_condition: "EIGHT_TO_TEN_DAYS",
      payment_condition_other: "",
    },
  },
];

export const ARRIVAL_CONTRACT_SHORTCUTS = [
  ...FARMER_ARRIVAL_CONTRACT_SHORTCUTS,
  ...SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS,
];

export function getContractShortcut(shortcutId) {
  return ARRIVAL_CONTRACT_SHORTCUTS.find((s) => s.id === shortcutId) ?? null;
}

export function isSupplierArrivalShortcut(shortcutId) {
  return String(shortcutId || "").startsWith("supplier-");
}

export function isFarmerArrivalQuickPreset(shortcutId) {
  return String(shortcutId || "").startsWith("farmer-");
}

/** Human-readable summary of preset fields (read-only in quick-create form). */
export function quickPresetSummaryLines(form, shortcutId) {
  const sc = getContractShortcut(shortcutId);
  if (!sc) return [];
  const cd = form.cd_applicable
    ? `CD ${form.cd_percent}%`
    : "No CD";
  const payment = labelFor(PAYMENT_CONDITION_OPTIONS, form.payment_condition);
  const bardana = labelFor(BARDANA_OPTIONS, form.bardana);
  const delivery = labelFor(DELIVERY_OPTIONS, form.delivery_condition);
  const ptype = labelFor(PURCHASE_TYPES_ARRIVAL, form.purchase_type);
  const bardanaLine =
    form.purchase_type === "MANDI_PURCHASE"
      ? `${bardana} · party bardana chart · ${delivery}`
      : `${bardana} · ${labelFor(BARDANA_DEDUCTION_OPTIONS, form.bardana_deduction)} · ${delivery}`;
  const partyKind = form.party_kind === "SUPPLIER" ? "Supplier" : "Farmer";
  return [`${partyKind} · Arrival · ${ptype}`, `${cd} · ${payment}`, bardanaLine];
}

/** Apply a shortcut; keeps party, gate pass, item lines, date if already chosen. */
export function applyContractShortcut(shortcutId, preserve = {}) {
  const shortcut = getContractShortcut(shortcutId);
  if (!shortcut) return emptyPurchaseContractForm();
  const base = emptyPurchaseContractForm();
  const keptItems =
    Array.isArray(preserve.items) && preserve.items.length > 0
      ? preserve.items
      : [emptyContractItemLine()];
  return {
    ...base,
    ...shortcut.preset,
    contract_date: preserve.contract_date || base.contract_date,
    party_id: preserve.party_id ?? "",
    gate_pass_id: preserve.gate_pass_id ?? "",
    quality: "",
    price: "",
    items: keptItems,
  };
}

/** Quick-create form → batch API (one contract per variety line). */
export function buildQuickPresetBatchPayload(form) {
  const filled = (form.items || []).filter(
    (line) => String(line.quality || "").trim() && line.price !== "" && line.price != null,
  );
  const { quality, quantity, price, items, ...shared } = form;
  return buildPurchaseContractBatchPayload({ ...shared, items: filled });
}

export function countQuickPresetItemLines(form) {
  return (form.items || []).filter(
    (line) => String(line.quality || "").trim() && line.price !== "" && line.price != null,
  ).length;
}

let _lineKey = 0;
export function emptyContractItemLine() {
  _lineKey += 1;
  return {
    _key: `line-${_lineKey}`,
    quality: "",
    quantity: "",
    price: "",
  };
}

/** Shared header for multi-item batch (party, payment, default bardana — no single quality/qty). */
export function emptyPurchaseContractBatchForm() {
  const base = emptyPurchaseContractForm();
  return {
    contract_phase: base.contract_phase,
    contract_date: base.contract_date,
    party_kind: base.party_kind,
    party_id: base.party_id,
    purchase_type: base.purchase_type,
    gate_pass_id: base.gate_pass_id,
    bardana: base.bardana,
    bardana_deduction: base.bardana_deduction,
    bardana_return: base.bardana_return,
    delivery_condition: base.delivery_condition,
    freight_rate_per_qtl: base.freight_rate_per_qtl,
    cd_applicable: base.cd_applicable,
    cd_percent: base.cd_percent,
    broker_applicable: base.broker_applicable,
    broker_party_id: base.broker_party_id,
    brokerage: base.brokerage,
    our_side_brokerage_basis: base.our_side_brokerage_basis,
    our_side_brokerage_rate: base.our_side_brokerage_rate,
    party_side_brokerage_basis: base.party_side_brokerage_basis,
    party_side_brokerage_rate: base.party_side_brokerage_rate,
    party_side_collection_method: base.party_side_collection_method,
    payment_condition: base.payment_condition,
    payment_condition_other: base.payment_condition_other,
    stock_sample_analysed: base.stock_sample_analysed,
    stack_sample_lab_report_id: base.stack_sample_lab_report_id,
    quality_conditions: base.quality_conditions,
    items: [emptyContractItemLine()],
  };
}

export function buildPurchaseContractBatchPayload(form) {
  const phase = form.contract_phase || "PRE_ARRIVAL";
  const isStock = form.purchase_type === "STOCK_PURCHASE";
  const sampleAnalysed = form.stock_sample_analysed === "yes";
  const isArrival = phase === "ARRIVAL";
  const onArrivalDirect = isArrival && form.purchase_type === "ON_ARRIVAL_DIRECT_PURCHASE";

  const payload = {
    contract_phase: phase,
    contract_date: form.contract_date || todayIsoDate(),
    party_kind: form.party_kind,
    party_id: Number(form.party_id),
    purchase_type: form.purchase_type,
    bardana: form.bardana,
    ...(form.purchase_type !== "MANDI_PURCHASE" ? { bardana_deduction: form.bardana_deduction } : {}),
    ...(form.purchase_type === "FOR_FACTORY" ? { bardana_return: form.bardana_return !== false } : {}),
    weight_basis: form.purchase_type === "MANDI_PURCHASE" ? form.weight_basis || "FIXED_PER_BAG" : null,
    delivery_condition: form.delivery_condition,
    freight_rate_per_qtl: form.freight_rate_per_qtl !== "" ? Number(form.freight_rate_per_qtl) : null,
    cd_applicable: form.cd_applicable,
    cd_percent: form.cd_applicable ? Number(form.cd_percent) : null,
    broker_applicable: form.broker_applicable,
    broker_party_id: form.broker_applicable ? Number(form.broker_party_id) : null,
    brokerage:
      form.broker_applicable && form.brokerage !== "" && form.brokerage != null
        ? Number(form.brokerage)
        : null,
    ...splitBrokeragePayload(form),
    payment_condition: form.payment_condition,
    payment_condition_other: form.payment_condition === "OTHER" ? form.payment_condition_other.trim() : null,
    quality_conditions: form.quality_conditions.trim() || null,
    items: (form.items || []).map((line) => ({
      quality: String(line.quality || "").trim(),
      quantity: onArrivalDirect ? 0 : Number(line.quantity),
      price: Number(line.price),
    })),
  };

  if (isArrival) {
    payload.gate_pass_id = Number(form.gate_pass_id);
  }

  if (!isArrival && isStock) {
    payload.stock_sample_analysed = sampleAnalysed;
    if (sampleAnalysed) {
      payload.stack_sample_lab_report_id = Number(form.stack_sample_lab_report_id);
    }
  }

  return payload;
}
